import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import { FormType } from 'src/components/pages/CoiFormManagement/types';
import type { FormManagementQuestionsQuery } from 'src/graphql';
import { useFormManagementQuestionnaireByIdQuery } from 'src/graphql';
import type { LanguageOption } from 'src/utils/languages';
import { DUMMY_DEFAULT_LANGUAGE_ONLY } from '../constants';
import { stripFormNonDefaultQuestionTranslationsForDuplicate } from '../helpers/stripFormNonDefaultQuestionTranslationsForDuplicate';
import { stripHtml } from '../helpers/validation';
import {
  mapApiQuestionnaireToFormState,
  mapApiQuestionsToFormState,
} from '../mapApiToFormState';
import type {
  DuplicateFromState,
  FormCreationMode,
  QuestionData,
} from '../types';

type FormCreationLocationState =
  | { duplicateFrom?: DuplicateFromState; formId?: string; returnTo?: string }
  | undefined;

export type UseFormCreationDuplicateFromLocationParams = {
  locationState: FormCreationLocationState;
  formType: FormType | null;
  isSpeakUpForm: boolean;
  setDuplicateManagementSessionActive: Dispatch<SetStateAction<boolean>>;
  setCategoryName: Dispatch<SetStateAction<string>>;
  setTitle: Dispatch<SetStateAction<string>>;
  setDescription: Dispatch<SetStateAction<string>>;
  setDuplicateQuestionsSourceId: Dispatch<SetStateAction<string | null>>;
  setMode: Dispatch<SetStateAction<FormCreationMode>>;
  setSelectedLanguages: Dispatch<SetStateAction<LanguageOption[]>>;
  duplicateQuestionsSourceId: string | null;
  isDuplicatePrefetchQuestionsLoading: boolean;
  duplicatePrefetchQuestionsData: FormManagementQuestionsQuery | undefined;
  title: string;
  categoryName: string;
  description: string;
  categoryDescription: string;
  selectedLanguages: LanguageOption[];
  setQuestions: Dispatch<SetStateAction<QuestionData[]>>;
  lastSavedStateRef: MutableRefObject<{
    title: string;
    description: string;
    categoryName: string;
    categoryDescription: string;
    questions: QuestionData[];
    selectedLanguages: LanguageOption[];
    selectedCountryCodes: string[];
    autoReviewEnabled: boolean;
  }>;
};

export const useFormCreationDuplicateFromLocation = ({
  locationState,
  formType,
  isSpeakUpForm,
  setDuplicateManagementSessionActive,
  setCategoryName,
  setTitle,
  setDescription,
  setDuplicateQuestionsSourceId,
  setMode,
  setSelectedLanguages,
  duplicateQuestionsSourceId,
  isDuplicatePrefetchQuestionsLoading,
  duplicatePrefetchQuestionsData,
  title,
  categoryName,
  description,
  categoryDescription,
  selectedLanguages,
  setQuestions,
  lastSavedStateRef,
}: UseFormCreationDuplicateFromLocationParams) => {
  const appliedDuplicateRef = useRef(false);
  const displayLabelRef = useRef<string | null>(null);

  const duplicateFrom = locationState?.duplicateFrom;
  const sourceQuestionnaireId = duplicateFrom?.sourceQuestionnaireId?.trim();

  const {
    data: sourceQuestionnaireData,
    isLoading: isSourceQuestionnaireLoading,
  } = useFormManagementQuestionnaireByIdQuery(
    { id: sourceQuestionnaireId! },
    { enabled: Boolean(sourceQuestionnaireId) },
  );

  useEffect(() => {
    const duplicateFrom = locationState?.duplicateFrom;
    if (!duplicateFrom || appliedDuplicateRef.current) return;

    appliedDuplicateRef.current = true;
    const displayLabel =
      duplicateFrom.displayName ?? `Copy of ${duplicateFrom.sourceTitle}`;
    displayLabelRef.current = displayLabel;
    const isManagementDuplicateWithQuestions = Boolean(
      duplicateFrom.sourceQuestionnaireId?.trim(),
    );

    if (isManagementDuplicateWithQuestions) {
      setDuplicateManagementSessionActive(true);
      if (formType === FormType.SPEAK_UP) {
        setCategoryName('');
      } else {
        setTitle('');
      }
    } else if (formType === FormType.SPEAK_UP) {
      setCategoryName(displayLabel);
    } else {
      setTitle(displayLabel);
    }
    const duplicateQuestionSourceId =
      duplicateFrom.sourceQuestionnaireId?.trim();
    const hasDuplicateQuestionSource = Boolean(duplicateQuestionSourceId);
    if (hasDuplicateQuestionSource) {
      setDuplicateQuestionsSourceId(duplicateQuestionSourceId!);
      setMode('loading');
    } else if (duplicateFrom.displayName != null) {
      setMode('initial');
    } else {
      setMode('content');
    }
    const hasSourceLanguages = (duplicateFrom.sourceLanguages?.length ?? 0) > 0;
    const languages = hasSourceLanguages
      ? duplicateFrom.sourceLanguages!
      : DUMMY_DEFAULT_LANGUAGE_ONLY;
    setSelectedLanguages(languages);
  }, [
    locationState,
    formType,
    setDuplicateManagementSessionActive,
    setCategoryName,
    setTitle,
    setDuplicateQuestionsSourceId,
    setMode,
    setSelectedLanguages,
  ]);

  useEffect(() => {
    if (!duplicateQuestionsSourceId) return;
    if (isDuplicatePrefetchQuestionsLoading || isSourceQuestionnaireLoading)
      return;

    const raw =
      duplicatePrefetchQuestionsData?.formManagementQuestions?.questions ?? [];
    const loadedQuestions = mapApiQuestionsToFormState(raw);
    const questionsForState = isSpeakUpForm
      ? stripFormNonDefaultQuestionTranslationsForDuplicate(loadedQuestions)
      : loadedQuestions;

    // Strip IDs from duplicated questions since they belong to the source questionnaire
    // Generate temporary IDs for frontend state management
    // Build a map of old question IDs to new question IDs for branching logic remapping
    const oldIdToNewId = new Map<string, string>();

    const questionsWithoutIds = questionsForState.map((q) => {
      const newQuestionId = `temp-${crypto.randomUUID()}`;
      oldIdToNewId.set(q.id, newQuestionId);

      return {
        ...q,
        id: newQuestionId,
        options: q.options?.map((opt) => ({
          ...opt,
          id: `temp-${crypto.randomUUID()}`,
        })),
      };
    });

    // Remap branching logic: update nextAction in options to use new question IDs
    // nextAction format is "question:ID" for branching to specific questions
    const questionsWithRemappedBranching = questionsWithoutIds.map((q) => ({
      ...q,
      options: q.options?.map((opt) => {
        if (!opt.nextAction) return opt;

        // If nextAction starts with "question:", extract the ID and remap it
        if (opt.nextAction.startsWith('question:')) {
          const oldQuestionId = opt.nextAction.substring('question:'.length);
          const newQuestionId = oldIdToNewId.get(oldQuestionId);

          if (newQuestionId) {
            return {
              ...opt,
              nextAction: `question:${newQuestionId}`,
            };
          }
        }

        return opt;
      }),
    }));

    setQuestions(questionsWithRemappedBranching);

    const sourceQuestionnaire =
      sourceQuestionnaireData?.formManagementQuestionnaireById;
    const sourceFormState = sourceQuestionnaire
      ? mapApiQuestionnaireToFormState(sourceQuestionnaire)
      : null;

    if (displayLabelRef.current) {
      setTitle(displayLabelRef.current);
    }

    if (sourceFormState) {
      setDescription(sourceFormState.description);
    }

    setMode('content');
    setDuplicateQuestionsSourceId(null);

    const sourceDescription = sourceFormState?.description ?? '';
    const strippedDescription = stripHtml(sourceDescription).trim();
    const strippedCategoryDescription = stripHtml(categoryDescription).trim();
    lastSavedStateRef.current = {
      title: displayLabelRef.current ?? title,
      description: strippedDescription,
      categoryName,
      categoryDescription: strippedCategoryDescription,
      questions: JSON.parse(JSON.stringify(questionsWithoutIds)),
      selectedLanguages,
      selectedCountryCodes: [],
      // VAULT-19456: Auto review is locked to enabled by default
      autoReviewEnabled: formType === FormType.CONFLICT_OF_INTEREST,
    };
  }, [
    duplicateQuestionsSourceId,
    isDuplicatePrefetchQuestionsLoading,
    isSourceQuestionnaireLoading,
    duplicatePrefetchQuestionsData,
    sourceQuestionnaireData,
    title,
    categoryName,
    description,
    categoryDescription,
    selectedLanguages,
    isSpeakUpForm,
    formType,
    lastSavedStateRef,
    setDuplicateQuestionsSourceId,
    setMode,
    setQuestions,
    setTitle,
    setDescription,
  ]);
};
